import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import "core-js/modules/es.array.slice.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/es.regexp.to-string.js";
import { MessageType, ProtocolVersion, Messages, formatMessage, MessageRole } from '../../constants';
import { createPingResponder } from '../heartbeat/ping';
import { SyncHook } from '../../utils/hooks';
import { warnClientServerIgnoredMessageWhenClosedOnce } from '../../utils/warnings';

/**
 * Pending request awaiting response
 */

/**
 * RequestIframeEndpointInbox
 *
 * The endpoint "inbox" is responsible for handling inbound framework messages and
 * driving pending request resolution by requestId.
 *
 * - Registers dispatcher handlers for ACK/ASYNC/RESPONSE/ERROR/STREAM_START + PING/PONG
 * - Manages pending requests via hub.pending
 */
export class RequestIframeEndpointInbox {
  constructor(hub, versionValidator) {
    _defineProperty(this, "hooks", {
      inbound: new SyncHook(),
      pendingRegistered: new SyncHook(),
      pendingUnregistered: new SyncHook(),
      pendingResolved: new SyncHook(),
      missingPending: new SyncHook()
    });
    this.hub = hub;
    this.versionValidator = versionValidator !== null && versionValidator !== void 0 ? versionValidator : hub.versionValidator;
  }

  /**
   * Register message handlers on hub.
   */
  registerHandlers() {
    var handlerOptions = this.hub.createHandlerOptions(this.handleVersionError.bind(this));
    handlerOptions.versionValidator = this.versionValidator;
    var boundHandleClientResponse = this.handleClientResponse.bind(this);
    this.hub.registerHandler(MessageType.ACK, boundHandleClientResponse, handlerOptions);
    this.hub.registerHandler(MessageType.ASYNC, boundHandleClientResponse, handlerOptions);
    this.hub.registerHandler(MessageType.RESPONSE, boundHandleClientResponse, handlerOptions);
    this.hub.registerHandler(MessageType.ERROR, boundHandleClientResponse, handlerOptions);
    this.hub.registerHandler(MessageType.PONG, this.handlePong.bind(this), handlerOptions);
    this.hub.registerHandler(MessageType.PING, this.handlePing.bind(this), handlerOptions);
    this.hub.registerHandler(MessageType.STREAM_START, boundHandleClientResponse, handlerOptions);
  }

  /**
   * Client-side "isConnect" ping helper.
   *
   * - Sends PING(requireAck=true)
   * - Resolves true when ACK or PONG arrives, false on timeout
   */
  pingIsConnect(params) {
    var requestId = `ping_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    return new Promise(resolve => {
      var timeoutId = this.hub.pending.setTimeout(() => {
        this.hub.pending.delete(RequestIframeEndpointInbox.PENDING_IS_CONNECT, requestId);
        resolve(false);
      }, params.timeoutMs);
      this.hub.pending.set(RequestIframeEndpointInbox.PENDING_IS_CONNECT, requestId, {
        resolve,
        timeoutId,
        targetOrigin: params.targetOrigin,
        originValidator: params.originValidator,
        onPeerId: params.onPeerId
      });
      params.peer.sendMessage(MessageType.PING, requestId, {
        requireAck: true,
        targetId: params.targetId
      });
    });
  }

  /**
   * Register pending request awaiting response
   */
  registerPendingRequest(requestId, resolve, reject, origin, originValidator) {
    this.hub.pending.set(RequestIframeEndpointInbox.PENDING_REQUESTS, requestId, {
      resolve,
      reject,
      origin,
      originValidator
    });
    this.hooks.pendingRegistered.call(requestId, {
      origin
    });
  }

  /**
   * Cancel pending response
   */
  unregisterPendingRequest(requestId) {
    this.hub.pending.delete(RequestIframeEndpointInbox.PENDING_REQUESTS, requestId);
    this.hooks.pendingUnregistered.call(requestId);
  }
  handlePing(data, context) {
    var _this$hub$instanceId;
    this.hooks.inbound.call(data, context);
    var responder = createPingResponder({
      hub: this.hub,
      handledBy: (_this$hub$instanceId = this.hub.instanceId) !== null && _this$hub$instanceId !== void 0 ? _this$hub$instanceId : MessageRole.CLIENT,
      includeTargetId: true
    });
    responder(data, context);
  }

  /**
   * Handle protocol version error
   */
  handleVersionError(data, context, version) {
    void context;
    var pending = this.hub.pending.get(RequestIframeEndpointInbox.PENDING_REQUESTS, data.requestId);
    if (pending) {
      this.hub.pending.delete(RequestIframeEndpointInbox.PENDING_REQUESTS, data.requestId);
      pending.reject(new Error(formatMessage(Messages.PROTOCOL_VERSION_TOO_LOW, version, ProtocolVersion.MIN_SUPPORTED)));
    }
  }

  /**
   * Handle client response
   */
  handleClientResponse(data, context) {
    var _this$hub$instanceId4;
    this.hooks.inbound.call(data, context);

    /**
     * isConnect waiter:
     * - ACK is sent by the peer when it accepted the PING (auto-ack workflow)
     * - We treat either ACK or PONG as a successful connectivity signal
     */
    if (data.type === MessageType.ACK) {
      var pendingIsConnect = this.hub.pending.get(RequestIframeEndpointInbox.PENDING_IS_CONNECT, data.requestId);
      if (pendingIsConnect) {
        var _this$hub$instanceId3, _pendingIsConnect$onP;
        // Must match the pending target origin (and originValidator if configured)
        if (!this.hub.isOriginAllowedBy(context.origin, data, context, pendingIsConnect.targetOrigin, pendingIsConnect.originValidator)) {
          var _this$hub$instanceId2;
          // Mark handled (and auto-ack if requireAck) but ignore the signal
          context.markAcceptedBy((_this$hub$instanceId2 = this.hub.instanceId) !== null && _this$hub$instanceId2 !== void 0 ? _this$hub$instanceId2 : MessageRole.CLIENT);
          return;
        }
        context.markAcceptedBy((_this$hub$instanceId3 = this.hub.instanceId) !== null && _this$hub$instanceId3 !== void 0 ? _this$hub$instanceId3 : MessageRole.CLIENT);
        this.hub.pending.clearTimeout(pendingIsConnect.timeoutId);
        this.hub.pending.delete(RequestIframeEndpointInbox.PENDING_IS_CONNECT, data.requestId);
        (_pendingIsConnect$onP = pendingIsConnect.onPeerId) === null || _pendingIsConnect$onP === void 0 || _pendingIsConnect$onP.call(pendingIsConnect, data.creatorId);
        pendingIsConnect.resolve(true);
        return;
      }
    }
    var pending = this.hub.pending.get(RequestIframeEndpointInbox.PENDING_REQUESTS, data.requestId);
    if (!pending) {
      /**
       * Pending request not found - ignore by default.
       *
       * If endpoint is already closed/destroyed, emit a warning to help debugging:
       * this usually means the client was recreated/unmounted before the response arrived.
       */
      if (!this.hub.isOpen) {
        this.hooks.missingPending.call(data, context);
        warnClientServerIgnoredMessageWhenClosedOnce(this.hub, {
          type: data.type,
          requestId: data.requestId
        });
      }
      return;
    }

    /** Validate origin */
    if (!this.hub.isOriginAllowedBy(context.origin, data, context, pending.origin, pending.originValidator)) {
      return;
    }

    /**
     * Mark as handled so:
     * - other client instances sharing the same channel won't also process it
     * - MessageDispatcher can run its generalized requireAck auto-ack logic
     */
    context.markAcceptedBy((_this$hub$instanceId4 = this.hub.instanceId) !== null && _this$hub$instanceId4 !== void 0 ? _this$hub$instanceId4 : MessageRole.CLIENT);

    /** ack, async, and stream_start don't delete pending (stream_start needs to keep pending for stream_data/stream_end) */
    if (data.type === MessageType.ACK || data.type === MessageType.ASYNC || data.type === MessageType.STREAM_START) {
      this.hooks.pendingResolved.call(data.requestId, data);
      pending.resolve(data);
      return;
    }

    /** response and error delete pending */
    this.hub.pending.delete(RequestIframeEndpointInbox.PENDING_REQUESTS, data.requestId);
    this.hooks.pendingResolved.call(data.requestId, data);
    pending.resolve(data);
  }

  /**
   * Handle PONG:
   * - If it matches an existing pending request, resolve it.
   * - Otherwise, ignore (facade may still use PONG for isConnect waiter).
   */
  handlePong(data, context) {
    var _this$hub$instanceId7;
    this.hooks.inbound.call(data, context);
    var pendingIsConnect = this.hub.pending.get(RequestIframeEndpointInbox.PENDING_IS_CONNECT, data.requestId);
    if (pendingIsConnect) {
      var _this$hub$instanceId6, _pendingIsConnect$onP2;
      if (!this.hub.isOriginAllowedBy(context.origin, data, context, pendingIsConnect.targetOrigin, pendingIsConnect.originValidator)) {
        var _this$hub$instanceId5;
        context.markAcceptedBy((_this$hub$instanceId5 = this.hub.instanceId) !== null && _this$hub$instanceId5 !== void 0 ? _this$hub$instanceId5 : MessageRole.CLIENT);
        return;
      }
      context.markAcceptedBy((_this$hub$instanceId6 = this.hub.instanceId) !== null && _this$hub$instanceId6 !== void 0 ? _this$hub$instanceId6 : MessageRole.CLIENT);
      this.hub.pending.clearTimeout(pendingIsConnect.timeoutId);
      this.hub.pending.delete(RequestIframeEndpointInbox.PENDING_IS_CONNECT, data.requestId);
      (_pendingIsConnect$onP2 = pendingIsConnect.onPeerId) === null || _pendingIsConnect$onP2 === void 0 || _pendingIsConnect$onP2.call(pendingIsConnect, data.creatorId);
      pendingIsConnect.resolve(true);
      return;
    }
    var pending = this.hub.pending.get(RequestIframeEndpointInbox.PENDING_REQUESTS, data.requestId);
    if (!pending) return;
    if (!this.hub.isOriginAllowedBy(context.origin, data, context, pending.origin, pending.originValidator)) {
      return;
    }
    context.markAcceptedBy((_this$hub$instanceId7 = this.hub.instanceId) !== null && _this$hub$instanceId7 !== void 0 ? _this$hub$instanceId7 : MessageRole.CLIENT);
    this.hub.pending.delete(RequestIframeEndpointInbox.PENDING_REQUESTS, data.requestId);
    this.hooks.pendingResolved.call(data.requestId, data);
    pending.resolve(data);
  }
}
_defineProperty(RequestIframeEndpointInbox, "PENDING_REQUESTS", 'inbox:pendingRequests');
_defineProperty(RequestIframeEndpointInbox, "PENDING_IS_CONNECT", 'inbox:pendingIsConnect');